import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { PlanBadge } from "../ui";
import Icon from "../ui/icons";
import Logo from "../../assets/logo-mark.png";
import "./NavBar.css";

const SunIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="4.2" />
    <path d="M12 2v2.6M12 19.4V22M2 12h2.6M19.4 12H22M4.9 4.9l1.9 1.9M17.2 17.2l1.9 1.9M19.1 4.9l-1.9 1.9M6.8 17.2l-1.9 1.9" />
  </svg>
);

const MoonIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20.5 14.2A8.5 8.5 0 1 1 9.8 3.5a7 7 0 0 0 10.7 10.7z" />
  </svg>
);

export default function NavBar({ theme, onToggleTheme }) {
  const { isAuthed, user, entitlements, isStaff, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  // A route change closes the mobile sheet; without this it stays open over
  // the page the user just navigated to.
  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (e) => e.key === "Escape" && setNavOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [navOpen]);

  async function onLogout() {
    setMenuOpen(false);
    await logout();
    navigate("/");
  }

  const links = [
    { to: "/", label: "Generator", end: true, show: true },
    { to: "/settings", label: "Card settings", show: true },
    { to: "/pricing", label: "Pricing", show: true },
    { to: "/about", label: "About", show: true },
    { to: "/history", label: "History", show: isAuthed },
    { to: "/photos", label: "Photos", show: isAuthed },
    { to: "/support", label: "Tickets", show: isAuthed },
    { to: "/staff", label: "Queue", show: isStaff },
    { to: "/admin", label: "Admin", show: isAdmin },
  ].filter((l) => l.show);

  const remaining = entitlements?.windowRemaining;
  const credits = entitlements?.credits ?? 0;

  return (
    <div className={`navwrap${scrolled ? " is-scrolled" : ""}`}>
      <header className="nav">
        <div className="nav-inner">
          <Link className="nav-brand" to="/" aria-label="Foliox home">
            <img className="nav-mark" src={Logo} alt="" />
            <span className="nav-word">
              FOL<span>IOX</span>
            </span>
          </Link>

          <nav className="nav-links" aria-label="Main">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="nav-right">
            {isAuthed && (
              <div className="nav-meters">
                <span
                  className={`nav-meter${remaining === 0 ? " is-out" : ""}`}
                  title={
                    remaining === -1
                      ? "Unlimited generations on your plan"
                      : `${remaining ?? 0} generations left today`
                  }
                >
                  <Icon.Bolt />
                  {remaining === -1 ? "∞" : (remaining ?? 0)}
                </span>

                {credits > 0 && (
                  <span
                    className="nav-meter is-credit"
                    title={`${credits} top-up credit${credits === 1 ? "" : "s"}`}
                  >
                    <Icon.Coin />
                    {credits}
                  </span>
                )}
              </div>
            )}

            <button
              type="button"
              className="nav-icon"
              onClick={onToggleTheme}
              aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
              title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
            >
              {theme === "dark" ? <SunIcon /> : <MoonIcon />}
            </button>

            {isAuthed ? (
              <div className="nav-user" ref={menuRef}>
                <button
                  type="button"
                  className="nav-avatar"
                  onClick={() => setMenuOpen((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                >
                  {user.username.slice(0, 2).toUpperCase()}
                </button>
                {menuOpen && (
                  <div className="nav-menu" role="menu">
                    <div className="nav-menu-head">
                      <strong>{user.username}</strong>
                      <PlanBadge plan={entitlements?.plan} />
                    </div>
                    <Link to="/profile" role="menuitem" onClick={() => setMenuOpen(false)}>
                      Profile
                    </Link>
                    <Link to="/history" role="menuitem" onClick={() => setMenuOpen(false)}>
                      History
                    </Link>
                    <Link to="/photos" role="menuitem" onClick={() => setMenuOpen(false)}>
                      Photo library
                    </Link>
                    <Link to="/support" role="menuitem" onClick={() => setMenuOpen(false)}>
                      Tickets
                    </Link>
                    {entitlements?.plan?.apiAccess && (
                      <Link to="/api-docs" role="menuitem" onClick={() => setMenuOpen(false)}>
                        API &amp; docs
                      </Link>
                    )}
                    <button type="button" role="menuitem" onClick={onLogout}>
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="nav-auth">
                <Link className="btn btn-sm" to="/login">
                  Sign in
                </Link>
                <Link className="btn btn-sm btn-primary" to="/register">
                  Sign up
                </Link>
              </div>
            )}

            <button
              type="button"
              className="nav-burger"
              aria-label="Menu"
              aria-expanded={navOpen}
              onClick={() => setNavOpen((v) => !v)}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>

        {navOpen && (
          <nav className="nav-sheet" aria-label="Main" onClick={() => setNavOpen(false)}>
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end}>
                {l.label}
              </NavLink>
            ))}
            {!isAuthed && (
              <>
                <NavLink to="/login">Sign in</NavLink>
                <NavLink to="/register">Sign up</NavLink>
              </>
            )}
          </nav>
        )}
      </header>
    </div>
  );
}
